'use client';

import { useEffect, ReactNode, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/lib/context/User.context';
import Loader from '@/components/ui/Loader';
import { hasPaidAccess } from '@/lib/subscription-utils';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export default function ProtectedRoute({
  children,
  redirectTo = '/auth/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, loading, checkAuth, userProfile } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [isChecking, setIsChecking] = useState(true);

  // Define public routes that don't require authentication
  const publicRoutes = ['/contact'];
  
  // Define allowed routes for unpaid users (when trial has expired)
  const allowedRoutesForUnpaidUsers = ['/profile-settings', '/dashboard', "/contact"];

  // Function to check if user has paid access (active subscription or valid trial)
  const hasValidAccess = (): boolean => {
    if (!userProfile) return false;
    return hasPaidAccess(userProfile.subscription, userProfile.role);
  };

  useEffect(() => {
    const verifyAuth = async () => {
      // Check if current route is public (no authentication required)
      const isPublicRoute = publicRoutes.some(route =>
        pathname === route || pathname.startsWith(route + '/')
      );
      
      if (isPublicRoute) {
        setIsChecking(false);
        return;
      }

      // Skip if still loading initial auth state
      if (loading) {
        return;
      }

      // If already authenticated, check subscription status
      if (isAuthenticated && userProfile) {
        // Check if user has valid access (active subscription or valid trial)
        if (!hasValidAccess()) {
          // No valid access, check if current route is allowed for users without access
          const isAllowedRoute = allowedRoutesForUnpaidUsers.some(route =>
            pathname === route || pathname.startsWith(route + '/')
          );

          if (!isAllowedRoute) {
            // Redirect to subscription page if not on allowed route
            router.push('/profile-settings?page=subscription');
            return;
          }
        }
        setIsChecking(false);
        return;
      }

      // Otherwise, verify auth status
      try {
        const isValid = await checkAuth();
        if (!isValid) {
          router.push(redirectTo);
        } else {
          setIsChecking(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        router.push(redirectTo);
      }
    };

    verifyAuth();
  }, [loading, isAuthenticated, checkAuth, router, redirectTo, userProfile, pathname]);

  // Check if current route is public (no authentication required)
  const isPublicRoute = publicRoutes.some(route =>
    pathname === route || pathname.startsWith(route + '/')
  );
console.log(loading, isChecking, isAuthenticated);
  // Show loading while checking authentication
  if (loading || isChecking) {
    return (
      <div data-component-id="src/components/auth/ProtectedRoute.tsx:96:6" data-component-path="src/components/auth/ProtectedRoute.tsx" data-component-line="96" data-component-file="ProtectedRoute.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20min-h-screen%22%7D" className="flex items-center justify-center min-h-screen">
        <Loader data-component-id="src/components/auth/ProtectedRoute.tsx:97:8" data-component-path="src/components/auth/ProtectedRoute.tsx" data-component-line="97" data-component-file="ProtectedRoute.tsx" data-component-name="Loader" data-component-content="%7B%22elementName%22%3A%22Loader%22%7D" />
      </div>
    );
  }

  // Allow access to public routes without authentication
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Show loading while redirecting
  if (!isAuthenticated) {
    return (
      <div data-component-id="src/components/auth/ProtectedRoute.tsx:110:6" data-component-path="src/components/auth/ProtectedRoute.tsx" data-component-line="110" data-component-file="ProtectedRoute.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20min-h-screen%22%7D" className="flex items-center justify-center min-h-screen">
        <Loader data-component-id="src/components/auth/ProtectedRoute.tsx:111:8" data-component-path="src/components/auth/ProtectedRoute.tsx" data-component-line="111" data-component-file="ProtectedRoute.tsx" data-component-name="Loader" data-component-content="%7B%22elementName%22%3A%22Loader%22%7D" />
      </div>
    );
  }

  return <>{children}</>;
}